/**
 * IPSViolationsAlert - IPS violations alert section
 * Extracted from ConsolidatedHoldings.tsx (TASK-371 decomposition)
 *
 * TASK-723 (review 2026-07-29): this used to re-derive its OWN verdict from
 * `Math.abs(difference) >= 5` / `>= 2`, making it the fourth drift implementation in the
 * product and one that could contradict the Radar on the same allocation — its copy even
 * stated the constant ("outside tolerance (>5% drift)") as if it were the client's mandate.
 * The rows now arrive already classified by `GET /api/portfolio/drift/{clientId}` against
 * the client's own tolerance band, so this component only renders what that decided.
 *
 * The canonical classification is BINARY — a slot either breaches the band or it does not.
 * There is no "approaching the limit" tier to render, so the old warning band is gone
 * rather than being re-invented from a constant.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { AllocationRow } from '../index';
import Badge from '../atoms/Badge';

export interface IPSViolationsAlertProps {
  allocationTableData: AllocationRow[];
  totalValue: number;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `CHF ${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `CHF ${(value / 1000).toFixed(0)}K`;
  return `CHF ${value.toLocaleString('de-CH')}`;
};

const IPSViolationsAlert: React.FC<IPSViolationsAlertProps> = ({
  allocationTableData,
  totalValue,
}) => {
  // `status === 'critical'` IS the server-side breach flag. No threshold is applied here.
  const violations = allocationTableData.filter(row => row.status === 'critical');

  if (violations.length === 0) return null;

  return (
    <div className="border-2 rounded-lg p-6 bg-red-50 border-red-300">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-100">
          <AlertTriangle className="w-5 h-5 text-red-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-red-900">IPS Violations Detected</h3>
          <p className="text-sm text-red-700">
            {violations.length} sub-asset class{violations.length > 1 ? 'es' : ''} outside the
            client&apos;s tolerance band
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {violations.map((violation, idx) => (
          <div key={idx} className="bg-white border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-gray-900">{violation.category}</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Current: {violation.percentage.toFixed(1)}% • Target: {(violation.target ?? 0).toFixed(1)}% •
                  <span className="font-medium text-red-600"> Drift: {(violation.difference || 0) > 0 ? '+' : ''}{(violation.difference || 0).toFixed(1)}%</span>
                </p>
              </div>
              <div className="text-right">
                <Badge label="CRITICAL" variant="error" size="sm" />
                <div className="text-sm font-medium text-gray-900 mt-2">
                  {(violation.difference || 0) > 0 ? 'Reduce' : 'Add'} {formatCurrency(Math.abs(totalValue * (violation.difference || 0) / 100))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-md p-3 bg-red-100">
        <p className="text-sm text-red-800">
          <strong>Recommendation:</strong> Immediate rebalancing required to bring the
          portfolio back within IPS tolerance. Schedule a client meeting to discuss.
        </p>
      </div>
    </div>
  );
};

export default IPSViolationsAlert;
